import {absurd} from 'fp-ts/lib/function';
import {useCallback, useDebugValue, useEffect, useState} from 'react';
import {createAbortControl} from '~/library/toolbox';
import {useLogger} from '~/shared/logging';

const ASK_TIMEOUT = 10_000;

export type Permission = Permission.State | null;
export namespace Permission {
    export const isGranted = (s: Permission) => s === 'granted';
    export const isDenied = (s: Permission) => s === 'denied';
    export const isPrompt = (s: Permission) => s === 'prompt';
    export const isPending = (s: Permission): s is null => s === null;
    export const isReady = (s: Permission): s is Permission & string => s !== null;

    export interface Status extends EventTarget {
        state: Permission.State;
    }

    export type State = globalThis.PermissionState | 'unsupported';
    export type Name = keyof typeof PermissionRequestMethod;
}

const PermissionRequestMethod = {
    async camera(): Promise<boolean> {
        try {
            // if we ever need font-facing camera ('user' facingMode), then we'll have to rework usePermission
            // somehow to accept additional parameters
            // This was hard-coded to fix an issue were on some android devices user-facing camera would open in qr scanner
            await navigator.mediaDevices.getUserMedia({video: {facingMode: {exact: 'environment'}}, audio: false});
            return true;
        } catch (error) {
            if (error instanceof Error) {
                if (error.name !== 'NotAllowedError') {
                    reportError(error);
                }
            }
            return false;
        }
    },
} satisfies {
    [K in globalThis.PermissionName]?: () => Promise<boolean>;
};

export function usePermission(name: Permission.Name) {
    const [state, setState] = useState<Permission>(null);
    function handleChange(this: PermissionStatus) {
        log.info(`Status changed to '${this.state}'`);
        setState(this.state);
    }

    useDebugValue(state);

    const log = useLogger(`Permission for '${name}'`, 'hotpink');

    useEffect(() => {
        const [signal, abort] = createAbortControl();
        queryPermission(name).then(permission => {
            if (signal.aborted) {
                return; // effect cleaned up
            }
            permission.addEventListener('change', handleChange, {signal});
            log.info(`Status currently '${permission.state}'`);
            setState(permission.state);
        });
        return abort;
    }, [name]);

    const request = useCallback(async (): Promise<boolean> => {
        log.info('Checking status');
        const {state} = await queryPermission(name);
        log.info(`Status is '${state}'`);
        switch (state) {
            case 'denied':
                return false;
            case 'granted':
                return true;
            case 'prompt':
            case 'unsupported': {
                log.info('Requesting permission');
                const ask = PermissionRequestMethod[name];
                // permissions request can hang without showing any prompt to user
                const ok = await raceUntilTimeout(ask(), ASK_TIMEOUT).catch(() => {
                    log.fail(`Request timed out after ${ASK_TIMEOUT / 1000} seconds`);
                    return false;
                });
                log.info(`Request ${ok ? 'succeeded' : 'failed'}`);
                return ok;
            }
            default:
                return absurd<never>(state);
        }
    }, [name]);

    return [state, request] as const;
}

async function queryPermission(name: Permission.Name): Promise<Permission.Status> {
    if ('permissions' in navigator) {
        return await navigator.permissions.query({name});
    }
    return new (class PermissionStatusStub extends EventTarget {
        readonly state = 'unsupported';
    })();
}

function raceUntilTimeout<T>(promise: Promise<T>, timeout: number) {
    return raceUntilAbort(promise, AbortSignal.timeout(timeout));
}

function raceUntilAbort<T>(promise: Promise<T>, signal: AbortSignal) {
    return Promise.race([
        promise,
        new Promise<never>((_resolve, reject) => {
            if (signal.aborted) {
                reject(signal.reason);
                return;
            }
            signal.addEventListener('abort', () => {
                reject(signal.reason);
            });
        }),
    ]);
}
